import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Directive for handling keyboard interactions with elements.
 * 
 * This directive can be used to trigger actions when specific keys are pressed
 * while an element has focus. It's useful for implementing keyboard shortcuts,
 * form submissions on Enter, and other keyboard-based interactions.
 * 
 * Example usages:
 * 
 * 1. Basic usage (responds to Enter key):
 *    <input [xKeyboardAction]="'Enter'" (actionTriggered)="doSomething()">
 * 
 * 2. Multiple keys:
 *    <div [xKeyboardAction]="['Enter', 'Space']" (actionTriggered)="doSomething()">
 * 
 * 3. With control over event behavior:
 *    <input [xKeyboardAction]="'Enter'" [preventDefault]="true" [stopPropagation]="true" (actionTriggered)="submit()">
 */

@Directive({
  selector: '[xKeyboardAction]'
})
export class KeyboardActionDirective implements OnInit, OnDestroy {
  private subscription = new Subscription();
  
  @Input() xKeyboardAction: string | string[] = 'Enter';
  @Input() preventDefault = true;
  @Input() stopPropagation = false;
  @Output() actionTriggered = new EventEmitter<KeyboardEvent>();
  
  constructor(private el: ElementRef<HTMLElement>) {}
  
  ngOnInit(): void {
    const element = this.el.nativeElement;
    
    this.subscription.add(
      fromEvent<KeyboardEvent>(element, 'keydown')
        .pipe(
          filter(event => {
            if (Array.isArray(this.xKeyboardAction)) {
              return this.xKeyboardAction.includes(event.key);
            }
            return event.key === this.xKeyboardAction;
          })
        )
        .subscribe(event => {
          if (this.preventDefault) {
            event.preventDefault();
          }
          
          if (this.stopPropagation) {
            event.stopPropagation();
          }
          
          this.actionTriggered.emit(event);
        })
    );
  }
  
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}